import moment from 'moment';
import { capitalize } from 'lodash';
import { Button } from 'src/redesign/components/Button';
import { API_URL } from 'src/utils/transport';
import { PlanDetailsType } from 'src/types/types';

type SubscriptionStatusBannerProps = {
  subscription: any;
  site: any;
  siteId: string;
  isWixTrial: boolean;
  discountReason: string;
  isInFreeTrial: boolean;
  trialDaysRemaining: number;
  handleCancel: (e: any) => void;
  planDetails: PlanDetailsType | null;
};

const nameMapping = {
  featured: 'professional',
};

export const SubscriptionStatusBanner = ({
  subscription,
  site,
  siteId,
  isWixTrial,
  discountReason,
  isInFreeTrial,
  trialDaysRemaining,
  handleCancel,
  planDetails,
}: SubscriptionStatusBannerProps) => {
  // Check if user has ticket packs
  const hasTicketPacks = (planDetails?.pack_ticket_balance || 0) > 0;
  const totalTickets = planDetails?.pack_ticket_balance || 0;
  const totalEmails = planDetails?.pack_email_balance || 0;
  const activePacks = planDetails?.ticket_packs?.filter((p) => p.status === 'active') || [];

  // Wix Trial Banner
  if (isWixTrial) {
    return (
      <div className="flex justify-content-center mb-6">
        <div
          className="font-bold px-6 py-4 border-round shadow-6"
          style={{
            maxWidth: '750px',
            animation: 'fadeInScale 0.5s ease-out',
            backgroundColor: '#FEFEFE',
            border: '4px solid var(--brand-background-color)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div className="flex align-items-center justify-content-center gap-3">
            <i
              className="pi pi-sparkles"
              style={{
                fontSize: '1.5rem',
                color: 'var(--brand-background-color)',
                animation: 'pulse 2s ease-in-out infinite',
              }}
            ></i>
            <div className="text-center text-xl" style={{ color: '#1a1a1a' }}>
              {discountReason
                ? `Select a plan now to start your free trial and get your ${discountReason} discount!`
                : 'Select a plan now to start your free trial!'}
            </div>
            <i
              className="pi pi-sparkles"
              style={{
                fontSize: '1.5rem',
                color: 'var(--brand-background-color)',
                animation: 'pulse 2s ease-in-out infinite 1s',
              }}
            ></i>
          </div>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                'linear-gradient(90deg, transparent, rgba(157, 37, 220, 0.05), transparent)',
              animation: 'shimmer 3s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          ></div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return null;
  }

  // Free Plan Banner
  if (subscription?.billing_type === 'free') {
    return (
      <div
        className="p-4 border-round mb-4"
        style={{
          backgroundColor: hasTicketPacks ? '#f0f9ff' : '#f0fdf4',
          border: hasTicketPacks ? '2px solid #0284c7' : '2px solid #22c55e',
          position: 'relative',
        }}
      >
        <div className="grid align-items-center">
          <div className="col-12 md:col-9">
            <div className="flex align-items-center gap-2 mb-3">
              <i
                className={hasTicketPacks ? 'pi pi-ticket' : 'pi pi-star'}
                style={{ fontSize: '1.25rem', color: hasTicketPacks ? '#0284c7' : '#22c55e' }}
              ></i>
              <h3 className="font-semibold text-2xl m-0" style={{ color: '#2c3e50' }}>
                {hasTicketPacks ? "You're on Free Plan + Ticket Packs" : "You're on the Free Plan"}
              </h3>
            </div>

            {hasTicketPacks ? (
              <>
                <div className="flex gap-5">
                  <div className="flex align-items-center gap-2">
                    <span className="text-sm" style={{ color: '#5a6c7d' }}>
                      Tickets Available
                    </span>
                    <span className="text-xl font-bold" style={{ color: '#0284c7' }}>
                      {totalTickets.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex align-items-center gap-2">
                    <span className="text-sm" style={{ color: '#5a6c7d' }}>
                      Emails Available
                    </span>
                    <span className="text-xl font-bold" style={{ color: '#6b7280' }}>
                      {totalEmails.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex align-items-center gap-2">
                    <span className="text-sm" style={{ color: '#5a6c7d' }}>
                      Active Packs
                    </span>
                    <span className="text-xl font-bold" style={{ color: '#6b7280' }}>
                      {activePacks.length}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="m-0 mb-2" style={{ color: '#5a6c7d' }}>
                  Upgrade to unlock <strong style={{ color: '#22c55e' }}>powerful features</strong>{' '}
                  like unlimited events, advanced analytics, email automation, and custom branding.
                </p>

                <p className="m-0 text-sm" style={{ color: '#5a6c7d' }}>
                  Start selling tickets and grow your events with premium tools.
                </p>
              </>
            )}
          </div>

          <div className="col-12 md:col-3 text-center md:text-right">
            <Button
              priority="primary"
              size="large"
              onClick={() => {
                const element = document.querySelector('.grid');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              style={{
                backgroundColor: hasTicketPacks ? '#0284c7' : '#22c55e',
                borderColor: hasTicketPacks ? '#0284c7' : '#22c55e',
              }}
            >
              {hasTicketPacks ? 'View Plans' : 'Explore Plans'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Free Trial Banner
  if (isInFreeTrial) {
    return (
      <div
        className="p-4 border-round mb-4"
        style={{
          backgroundColor: '#f8f4fc',
          border: '2px solid var(--brand-background-color)',
          position: 'relative',
        }}
      >
        <div className="grid align-items-center">
          <div className="col-12 md:col-8">
            <div className="flex align-items-center gap-2 mb-3">
              <i
                className="pi pi-info-circle"
                style={{ fontSize: '1.25rem', color: 'var(--brand-background-color)' }}
              ></i>
              <h3 className="font-semibold text-2xl m-0" style={{ color: '#2c3e50' }}>
                Free Trial Active - {trialDaysRemaining} {trialDaysRemaining === 1 ? 'Day' : 'Days'}{' '}
                Remaining
              </h3>
            </div>

            <p className="m-0 mb-2" style={{ color: '#5a6c7d' }}>
              Your free trial ends on{' '}
              <strong style={{ color: 'var(--brand-background-color)' }}>
                {moment(site.free_trial_at).add(7, 'days').format('MMMM D, YYYY')}
              </strong>
            </p>

            <p className="m-0 text-sm" style={{ color: '#5a6c7d' }}>
              Choose a plan to continue accessing your premium features.
            </p>
          </div>

          <div className="col-12 md:col-4 text-center md:text-right">
            <Button
              priority="primary"
              size="large"
              onClick={() => {
                const element = document.querySelector('.grid');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              style={{
                backgroundColor: 'var(--brand-background-color)',
                borderColor: 'var(--brand-background-color)',
              }}
            >
              Choose Your Plan
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Trial Billing Type Banner
  if (subscription.billing_type === 'trial') {
    return (
      <div
        className="p-4 border-round mb-4"
        style={{
          backgroundColor: '#f8f4fc',
          border: '2px solid var(--brand-background-color)',
          position: 'relative',
        }}
      >
        <div className="grid align-items-center">
          <div className="col-12 md:col-8">
            <div className="flex align-items-center gap-2 mb-3">
              <i
                className="pi pi-sparkles"
                style={{ fontSize: '1.25rem', color: 'var(--brand-background-color)' }}
              ></i>
              <h3 className="font-semibold text-2xl m-0" style={{ color: '#2c3e50' }}>
                You're on a Trial Plan
              </h3>
            </div>

            <p className="m-0 mb-2" style={{ color: '#5a6c7d' }}>
              Experience all the{' '}
              <strong style={{ color: 'var(--brand-background-color)' }}>premium features</strong>{' '}
              including unlimited events, advanced analytics, email automation, and custom branding.
            </p>

            <p className="m-0 text-sm" style={{ color: '#5a6c7d' }}>
              Select a plan below to continue after your trial period ends.
            </p>
          </div>

          <div className="col-12 md:col-4 text-center md:text-right">
            <Button
              priority="primary"
              size="large"
              onClick={() => {
                const element = document.querySelector('.grid');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              style={{
                backgroundColor: 'var(--brand-background-color)',
                borderColor: 'var(--brand-background-color)',
              }}
            >
              Select Your Plan
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Active Subscription Banner
  return (
    <div
      className="p-4 border-round mb-4"
      style={{
        backgroundColor: hasTicketPacks ? '#f0f9ff' : '#f8f4fc',
        border: hasTicketPacks ? '2px solid #0284c7' : '2px solid var(--brand-background-color)',
        position: 'relative',
      }}
    >
      {/* Header Section */}
      <div className="grid align-items-center mb-3">
        <div className="col-12 md:col-9">
          <div className="flex align-items-center gap-2">
            <i
              className={hasTicketPacks ? 'pi pi-ticket' : 'pi pi-check-circle'}
              style={{
                fontSize: '1.25rem',
                color: hasTicketPacks ? '#0284c7' : 'var(--brand-background-color)',
              }}
            ></i>
            <h3 className="font-semibold text-2xl m-0" style={{ color: '#2c3e50' }}>
              Current Plan:{' '}
              {capitalize(
                nameMapping[subscription.billing_type] ||
                  subscription.billing_type.replace('-', ' '),
              )}
              {hasTicketPacks && ' + Ticket Packs'}
            </h3>
          </div>
        </div>
        {!subscription?.cancel_at_period_end && (
          <div className="col-12 md:col-3 text-center md:text-right">
            {subscription?.site?.type !== 'shopify' && subscription?.site?.type !== 'wix' && (
              <form
                action={`${API_URL}/api/v1/dashboard/subscriptions/portal?site=${siteId}`}
                method="POST"
              >
                <Button
                  type="submit"
                  priority="primary"
                  size="large"
                  style={{
                    backgroundColor: hasTicketPacks ? '#0284c7' : 'var(--brand-background-color)',
                    borderColor: hasTicketPacks ? '#0284c7' : 'var(--brand-background-color)',
                  }}
                >
                  Manage Billing
                </Button>
              </form>
            )}
            {subscription?.site?.type === 'wix' && (
              <Button
                priority="primary"
                size="large"
                onClick={() =>
                  window.open(
                    'https://premium.wix.com/wix/api/mpContainerStaticController?originWidgetName=billings',
                    '_blank',
                  )
                }
                style={{
                  backgroundColor: hasTicketPacks ? '#0284c7' : 'var(--brand-background-color)',
                  borderColor: hasTicketPacks ? '#0284c7' : 'var(--brand-background-color)',
                }}
              >
                Manage Billing / Cancel
              </Button>
            )}
            {subscription?.site?.type === 'shopify' && (
              <Button
                priority="primary"
                size="large"
                onClick={handleCancel}
                style={{
                  backgroundColor: '#dc2626',
                  borderColor: '#dc2626',
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Ticket Pack Stats */}
      {hasTicketPacks && (
        <div className="flex gap-5 mb-3">
          <div className="flex align-items-center gap-2">
            <span className="text-sm" style={{ color: '#5a6c7d' }}>
              Pack Tickets
            </span>
            <span className="text-xl font-bold" style={{ color: '#0284c7' }}>
              {totalTickets.toLocaleString()}
            </span>
          </div>
          <div className="flex align-items-center gap-2">
            <span className="text-sm" style={{ color: '#5a6c7d' }}>
              Pack Emails
            </span>
            <span className="text-xl font-bold" style={{ color: '#6b7280' }}>
              {totalEmails.toLocaleString()}
            </span>
          </div>
          <div className="flex align-items-center gap-2">
            <span className="text-sm" style={{ color: '#5a6c7d' }}>
              Active Packs
            </span>
            <span className="text-xl font-bold" style={{ color: '#6b7280' }}>
              {activePacks.length}
            </span>
          </div>
        </div>
      )}

      {/* Cancellation Warning or Renewal Info */}
      {subscription?.cancel_at_period_end ? (
        <div>
          <div
            className="p-3 border-round mb-3"
            style={{
              backgroundColor: '#fef2f2',
              border: '2px solid #dc2626',
            }}
          >
            <div className="flex align-items-start gap-2 mb-2">
              <i
                className="pi pi-exclamation-triangle"
                style={{ fontSize: '1.1rem', color: '#dc2626', marginTop: '2px', flexShrink: 0 }}
              ></i>
              <div>
                <p className="m-0 font-bold text-base" style={{ color: '#991b1b' }}>
                  Subscription Cancelled
                </p>
                <p className="m-0 mt-1" style={{ color: '#7f1d1d', fontSize: '14px' }}>
                  Your plan will remain active until{' '}
                  <strong>{moment(subscription.current_period_end).format('MMMM D, YYYY')}</strong>,
                  then you'll lose access to all premium features.
                </p>
              </div>
            </div>
            <p className="m-0 text-sm" style={{ color: '#991b1b' }}>
              Changed your mind? You can reactivate your subscription anytime before it expires.
            </p>
          </div>
          <div className="flex justify-content-center md:justify-content-end">
            {subscription?.site?.type !== 'shopify' && subscription?.site?.type !== 'wix' && (
              <form
                action={`${API_URL}/api/v1/dashboard/subscriptions/portal?site=${siteId}`}
                method="POST"
              >
                <Button
                  type="submit"
                  priority="primary"
                  size="large"
                  style={{
                    backgroundColor: '#22c55e',
                    borderColor: '#22c55e',
                  }}
                >
                  Reactivate Subscription
                </Button>
              </form>
            )}
            {subscription?.site?.type === 'wix' && (
              <Button
                priority="primary"
                size="large"
                onClick={() =>
                  window.open(
                    'https://premium.wix.com/wix/api/mpContainerStaticController?originWidgetName=billings',
                    '_blank',
                  )
                }
                style={{
                  backgroundColor: '#22c55e',
                  borderColor: '#22c55e',
                }}
              >
                Reactivate Subscription
              </Button>
            )}
            {subscription?.site?.type === 'shopify' && (
              <Button
                priority="primary"
                size="large"
                onClick={handleCancel}
                style={{
                  backgroundColor: '#22c55e',
                  borderColor: '#22c55e',
                }}
              >
                Reactivate
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>
          <p className="m-0 mb-2" style={{ color: '#5a6c7d' }}>
            Your plan automatically renews on{' '}
            <strong style={{ color: hasTicketPacks ? '#0284c7' : 'var(--brand-background-color)' }}>
              {moment(subscription.current_period_end).format('MMMM D, YYYY')}
            </strong>
          </p>

          <p className="m-0 text-sm" style={{ color: '#5a6c7d' }}>
            {hasTicketPacks
              ? 'You have extra capacity from ticket packs. You can upgrade your plan or purchase more packs anytime.'
              : 'You can upgrade or change your plan anytime below.'}
          </p>
        </>
      )}
    </div>
  );
};
